import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Flame,
  Shield,
  TrendingUp,
  CheckCircle2,
  Layers,
  ExternalLink,
  Copy,
  Check,
  AlertTriangle,
  Bot,
  Coins,
  Cpu,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import LiveTicker from "../components/aegis/LiveTicker";
import { useAuth } from "../contexts/AuthContext";
import {
  getAegisPricing,
  getAegisSupply,
  verifySolanaPayment,
  verifySolanaUsdcPayment,
  submitPayPalPayment,
  getMembership,
} from "../lib/api";

function formatProof(n) {
  if (n == null) return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  if (x >= 1_000_000) return `${(x / 1_000_000).toFixed(2)}M`;
  if (x >= 1_000) return `${(x / 1_000).toFixed(1)}k`;
  return Math.round(x).toLocaleString();
}

function formatApt(n) {
  if (n == null) return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  if (x >= 1_000) return `${(x / 1_000).toFixed(2)}k`;
  return x.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function CopyButton({ value, label = "address", testid }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setDone(true);
          toast.success(`${label} copied`);
          setTimeout(() => setDone(false), 1200);
        } catch {
          toast.error("Clipboard unavailable");
        }
      }}
      className="inline-flex items-center gap-1.5 px-2 py-1 border border-zinc-800 hover:border-amber-500/60 text-zinc-400 hover:text-amber-300 text-[10px] uppercase tracking-[0.2em] font-mono"
      title={done ? "copied" : `copy ${label}`}
    >
      {done ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {done ? "copied" : "copy"}
    </button>
  );
}

function TierIcon({ tier }) {
  if (tier.tier_id === "free") return <Sparkles className="h-5 w-5 text-emerald-300" />;
  if (tier.tier_id === "builder") return <Coins className="h-5 w-5 text-amber-300" />;
  if (tier.tier_id === "network_operator") return <Cpu className="h-5 w-5 text-sky-300" />;
  return <Layers className="h-5 w-5 text-zinc-400" />;
}

function FreeTierCard({ tier, currentTier }) {
  const isMine = currentTier === "free";
  return (
    <article
      data-testid="pricing-tier-free"
      className={`relative border ${isMine ? "border-emerald-500/60 bg-emerald-500/5" : "border-zinc-800 bg-[#0b0b0d]"} p-6 sm:p-7 flex flex-col`}
    >
      {isMine && (
        <span className="absolute -top-3 left-6 px-2 py-0.5 text-[9px] uppercase tracking-[0.3em] border border-emerald-500/60 bg-[#0b0b0d] text-emerald-300 font-mono">
          your tier
        </span>
      )}
      <div className="flex items-center gap-2">
        <TierIcon tier={tier} />
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono">
          {tier.label}
        </div>
      </div>
      <h3 className="font-heading text-2xl tracking-[0.1em] text-zinc-100 mt-1">
        {tier.name}
      </h3>
      <div className="mt-4 flex items-baseline gap-2">
        <div className="font-mono text-5xl text-emerald-300 leading-none">$0</div>
        <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-mono">forever</div>
      </div>
      <ul className="mt-5 space-y-2.5 text-sm text-zinc-300">
        {(tier.perks || []).map((p, i) => (
          <li key={i} className="flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      <div className="mt-auto pt-6">
        <button
          data-testid="pricing-cta-free"
          disabled
          className="w-full px-4 py-3 border border-emerald-500/40 text-emerald-200 text-[11px] uppercase tracking-[0.3em] font-mono disabled:opacity-60"
        >
          {isMine ? "active" : "sign up — already free"}
        </button>
      </div>
    </article>
  );
}

function NetworkOperatorCard({ tier }) {
  return (
    <article
      data-testid="pricing-tier-network-operator"
      className="relative border border-sky-500/30 bg-sky-500/[0.03] p-6 sm:p-7 flex flex-col"
    >
      <span className="absolute -top-3 left-6 px-2 py-0.5 text-[9px] uppercase tracking-[0.3em] border border-sky-500/60 bg-[#0b0b0d] text-sky-300 font-mono">
        coming soon
      </span>
      <div className="flex items-center gap-2">
        <TierIcon tier={tier} />
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono">
          {tier.label}
        </div>
      </div>
      <h3 className="font-heading text-2xl tracking-[0.1em] text-zinc-100 mt-1">
        {tier.name}
      </h3>
      <div className="mt-4 flex items-baseline gap-2">
        <div className="font-mono text-3xl text-sky-300 leading-none">attach your AI</div>
      </div>
      <ul className="mt-5 space-y-2.5 text-sm text-zinc-300">
        {(tier.perks || []).map((p, i) => (
          <li key={i} className="flex items-start gap-2">
            <Cpu className="h-4 w-4 text-sky-400 shrink-0 mt-0.5" />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      <div className="mt-auto pt-6">
        <button
          data-testid="pricing-cta-network-operator"
          disabled
          className="w-full px-4 py-3 border border-sky-500/40 text-sky-200 text-[11px] uppercase tracking-[0.3em] font-mono disabled:opacity-60"
        >
          coming soon
        </button>
      </div>
    </article>
  );
}

function AltPaymentRails({ usdcPayment, paypalPayment, isAuthed, onPaid }) {
  const [mode, setMode] = useState(null); // null | 'usdc' | 'paypal'
  const [usdcSig, setUsdcSig] = useState("");
  const [usdcSender, setUsdcSender] = useState("");
  const [ppTxId, setPpTxId] = useState("");
  const [ppEmail, setPpEmail] = useState("");
  const [ppNote, setPpNote] = useState("");
  const [busy, setBusy] = useState(false);

  const usdcMin = usdcPayment?.min_usd || 10;
  const ppMin = paypalPayment?.min_usd || 10;
  const ppLink = paypalPayment?.link || "https://www.paypal.me/Donaldgauthier435";

  const submitUsdc = async (e) => {
    e?.preventDefault?.();
    if (!isAuthed) return toast.error("Sign in first so we know which account to upgrade.");
    if (!usdcSig.trim()) return toast.error("Paste your Solana USDC transaction signature.");
    setBusy(true);
    try {
      const res = await verifySolanaUsdcPayment({
        signature: usdcSig.trim(),
        sender_wallet: usdcSender.trim() || null,
      });
      toast.success(
        `Builder tier unlocked · expires ${new Date(res.expires_at).toLocaleDateString()}`
      );
      setUsdcSig("");
      setUsdcSender("");
      onPaid?.(res);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail?.message || (typeof detail === "string" ? detail : null) || err?.message || "USDC verification failed");
    } finally {
      setBusy(false);
    }
  };

  const submitPayPal = async (e) => {
    e?.preventDefault?.();
    if (!isAuthed) return toast.error("Sign in first so we know which account to upgrade.");
    if (!ppTxId.trim()) return toast.error("Paste your PayPal transaction ID.");
    setBusy(true);
    try {
      const res = await submitPayPalPayment({
        transaction_id: ppTxId.trim(),
        payer_email: ppEmail.trim() || null,
        note: ppNote.trim() || null,
      });
      toast.success(res?.message || "PayPal payment submitted — Architect will approve within 24h.");
      setPpTxId("");
      setPpEmail("");
      setPpNote("");
      onPaid?.(res);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail?.message || (typeof detail === "string" ? detail : null) || err?.message || "PayPal submission failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 pt-5 border-t border-sky-500/30" data-testid="alt-payment-rails">
      <div className="text-[10px] uppercase tracking-[0.3em] text-sky-300 font-mono mb-2">
        Or pay in fiat · ${usdcMin} · fastest onboarding
      </div>
      <p className="text-[11.5px] text-zinc-400 leading-relaxed mb-3">
        No crypto experience? Send ${ppMin} PayPal or ${usdcMin} USDC on Solana
        — same 30-day Builder tier, one-tap start.
      </p>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <button
          type="button"
          data-testid="alt-pay-choose-paypal"
          onClick={() => setMode(mode === "paypal" ? null : "paypal")}
          className={`px-3 py-2 border text-[11px] uppercase tracking-[0.25em] font-mono ${
            mode === "paypal"
              ? "border-sky-400 text-sky-200 bg-sky-500/10"
              : "border-sky-500/40 text-sky-300/80 hover:border-sky-400 hover:text-sky-200"
          }`}
        >
          PayPal · ${ppMin}
        </button>
        <button
          type="button"
          data-testid="alt-pay-choose-usdc"
          onClick={() => setMode(mode === "usdc" ? null : "usdc")}
          className={`px-3 py-2 border text-[11px] uppercase tracking-[0.25em] font-mono ${
            mode === "usdc"
              ? "border-sky-400 text-sky-200 bg-sky-500/10"
              : "border-sky-500/40 text-sky-300/80 hover:border-sky-400 hover:text-sky-200"
          }`}
        >
          USDC · ${usdcMin}
        </button>
      </div>

      {mode === "paypal" && (
        <form onSubmit={submitPayPal} className="mt-3 space-y-2 border border-sky-500/20 bg-sky-500/[0.03] p-3" data-testid="paypal-form">
          <div className="text-[10px] uppercase tracking-[0.3em] text-sky-300 font-mono">
            Step 1 · Send ${ppMin} to
          </div>
          <a
            href={ppLink}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="paypal-link"
            className="inline-flex items-center gap-1.5 font-mono text-[11px] text-sky-200 hover:text-sky-100 break-all"
          >
            <ExternalLink className="h-3 w-3" /> {ppLink}
          </a>
          <div className="text-[10px] uppercase tracking-[0.3em] text-sky-300 font-mono mt-3">
            Step 2 · Paste your PayPal transaction ID
          </div>
          <input
            type="text"
            value={ppTxId}
            onChange={(e) => setPpTxId(e.target.value)}
            placeholder="e.g. 5GH12345XY678901A"
            data-testid="paypal-tx-id"
            className="w-full bg-black/40 border border-zinc-800 focus:border-sky-500/60 px-3 py-2 text-sm text-zinc-100 font-mono outline-none"
          />
          <input
            type="email"
            value={ppEmail}
            onChange={(e) => setPpEmail(e.target.value)}
            placeholder="Your PayPal email (helps the Architect match it)"
            data-testid="paypal-email"
            className="w-full bg-black/40 border border-zinc-800 focus:border-sky-500/60 px-3 py-2 text-xs text-zinc-300 font-mono outline-none"
          />
          <input
            type="text"
            value={ppNote}
            onChange={(e) => setPpNote(e.target.value)}
            maxLength={240}
            placeholder="Note for the Architect (optional)"
            data-testid="paypal-note"
            className="w-full bg-black/40 border border-zinc-800 focus:border-sky-500/60 px-3 py-2 text-xs text-zinc-400 font-mono outline-none"
          />
          <button
            type="submit"
            disabled={busy || !ppTxId.trim()}
            data-testid="paypal-submit"
            className="w-full px-4 py-3 border border-sky-500/60 text-sky-200 hover:border-sky-300 hover:text-sky-100 hover:bg-sky-500/10 text-[11px] uppercase tracking-[0.3em] font-mono disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> submitting…
              </>
            ) : (
              <>
                <Shield className="h-4 w-4" /> Submit PayPal · manual review
              </>
            )}
          </button>
          <p className="text-[10px] text-zinc-500 leading-relaxed">
            PayPal has no on-chain proof, so the Architect approves within
            24 hours after confirming the payment in the PayPal dashboard.
          </p>
        </form>
      )}

      {mode === "usdc" && (
        <form onSubmit={submitUsdc} className="mt-3 space-y-2 border border-sky-500/20 bg-sky-500/[0.03] p-3" data-testid="usdc-form">
          <div className="text-[10px] uppercase tracking-[0.3em] text-sky-300 font-mono">
            Step 1 · Send ${usdcMin} USDC (Solana mainnet) to
          </div>
          <div className="border border-zinc-800 bg-black/40 p-3 flex items-center gap-2">
            <span
              className="flex-1 font-mono text-[11px] text-sky-200 break-all"
              data-testid="usdc-wallet-address"
            >
              {usdcPayment?.wallet || "—"}
            </span>
            <CopyButton
              value={usdcPayment?.wallet || ""}
              label="USDC wallet"
              testid="copy-usdc-wallet"
            />
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-sky-300 font-mono mt-3">
            Step 2 · Paste your USDC transaction signature
          </div>
          <input
            type="text"
            value={usdcSig}
            onChange={(e) => setUsdcSig(e.target.value)}
            placeholder="e.g. 5KqGn7…rXk"
            data-testid="usdc-tx-signature"
            className="w-full bg-black/40 border border-zinc-800 focus:border-sky-500/60 px-3 py-2 text-sm text-zinc-100 font-mono outline-none"
          />
          <input
            type="text"
            value={usdcSender}
            onChange={(e) => setUsdcSender(e.target.value)}
            placeholder="Your Phantom wallet address (optional, helps AI verify)"
            data-testid="usdc-sender-wallet"
            className="w-full bg-black/40 border border-zinc-800 focus:border-sky-500/60 px-3 py-2 text-xs text-zinc-300 font-mono outline-none"
          />
          <button
            type="submit"
            disabled={busy || !usdcSig.trim()}
            data-testid="usdc-submit"
            className="w-full px-4 py-3 border border-sky-500/60 text-sky-200 hover:border-sky-300 hover:text-sky-100 hover:bg-sky-500/10 text-[11px] uppercase tracking-[0.3em] font-mono disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> AI auditor verifying on-chain…
              </>
            ) : (
              <>
                <Shield className="h-4 w-4" /> Verify USDC · unlock builder
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}


function BuilderTierCard({ tier, solanaPayment, usdcPayment, paypalPayment, currentTier, onPaid, isAuthed }) {
  const [sig, setSig] = useState("");
  const [sender, setSender] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [verdict, setVerdict] = useState(null);
  const isMine = currentTier === "builder";

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (!isAuthed) {
      toast.error("Sign in first so we know which account to upgrade.");
      return;
    }
    if (!sig.trim()) {
      toast.error("Paste your Solana transaction signature.");
      return;
    }
    setBusy(true);
    setVerdict(null);
    try {
      const res = await verifySolanaPayment({
        signature: sig.trim(),
        sender_wallet: sender.trim() || null,
        note: note.trim() || null,
      });
      setVerdict(res.verdict || null);
      toast.success(
        `Builder tier unlocked · expires ${new Date(res.expires_at).toLocaleDateString()}`
      );
      onPaid?.(res);
      setSig("");
      setSender("");
      setNote("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.verdict) setVerdict(detail.verdict);
      const msg =
        detail?.message ||
        (typeof detail === "string" ? detail : null) ||
        err?.message ||
        "Verification failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      data-testid="pricing-tier-builder"
      className={`relative border ${isMine ? "border-amber-500/60 bg-amber-500/5" : "border-amber-500/40 bg-amber-500/[0.03]"} p-6 sm:p-7 flex flex-col`}
    >
      <span className="absolute -top-3 left-6 px-2 py-0.5 text-[9px] uppercase tracking-[0.3em] border border-amber-500/60 bg-[#0b0b0d] text-amber-300 font-mono">
        {isMine ? "your tier" : "most popular"}
      </span>
      <div className="flex items-center gap-2">
        <TierIcon tier={tier} />
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono">
          {tier.label}
        </div>
      </div>
      <h3 className="font-heading text-2xl tracking-[0.1em] text-zinc-100 mt-1">
        {tier.name}
      </h3>
      <div className="mt-4 flex items-baseline gap-2">
        <div className="font-mono text-5xl text-amber-300 leading-none">
          {tier.price_aegis ?? tier.price_usd ?? "—"}
        </div>
        <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-mono">
          AEGIS · {tier.duration_days}d
        </div>
      </div>

      <ul className="mt-5 space-y-2.5 text-sm text-zinc-300">
        {(tier.perks || []).map((p, i) => (
          <li key={i} className="flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
            <span>{p}</span>
          </li>
        ))}
        <li className="flex items-start gap-2">
          <Flame className="h-4 w-4 text-rose-400 shrink-0 mt-0.5" />
          <span>
            Burns{" "}
            <span className="font-mono text-rose-300">
              {formatApt(tier.burn_on_purchase_apt)} APT
            </span>{" "}
            ({formatProof(tier.burn_on_purchase)} PROOF) from circulating supply
          </span>
        </li>
        <li className="flex items-start gap-2">
          <Bot className="h-4 w-4 text-sky-400 shrink-0 mt-0.5" />
          <span>
            AI auditor (Claude) verifies your transaction on-chain + flags Sybil patterns
          </span>
        </li>
      </ul>

      {/* v19.34 · Pay with on-site AEGIS · primary path */}
      <div className="mt-6 pt-5 border-t border-emerald-500/30">
        <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 font-mono mb-2">
          Pay from your earned AEGIS · recommended
        </div>
        <p className="text-[11.5px] text-zinc-400 leading-relaxed mb-3">
          Spend {solanaPayment?.min_aegis || 100} AEGIS from your on-site balance
          directly — no external wallet required. Debited oldest-first from
          truths you&apos;ve verified.
        </p>
        <Link
          to="/console/deposit"
          data-testid="pricing-cta-onsite-aegis"
          className="w-full px-4 py-3 border border-emerald-500/60 text-emerald-200 hover:bg-emerald-500 hover:text-zinc-950 text-[11px] uppercase tracking-[0.3em] font-mono inline-flex items-center justify-center gap-2"
        >
          <Shield className="h-4 w-4" /> Fund with on-site AEGIS →
        </Link>
      </div>

      {/* v19.40 · $10 alt rails moved to landing /donate section as of v19.40.2 —
          the fiat rails are now a donation ask, not a builder-tier purchase path.
          Kept the AltPaymentRails component here in the module because the Solana
          USDC / PayPal verify endpoints still exist; the invocation was removed. */}

      {/* Advanced · external Solana wallet path (kept for users who prefer on-chain) */}
      <details className="mt-6 pt-5 border-t border-amber-500/20 group">
        <summary className="cursor-pointer text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-amber-300 font-mono mb-3">
          Advanced · pay {solanaPayment?.min_aegis || 100} AEGIS from an external Solana wallet
        </summary>
        <form onSubmit={onSubmit} className="mt-3 space-y-3">
        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-mono">
          Step 1 · Send {solanaPayment?.min_aegis || solanaPayment?.min_usdc || 100} AEGIS on Solana to:
        </div>
        <div className="border border-zinc-800 bg-black/40 p-3 flex items-center gap-2 group">
          <span
            className="flex-1 font-mono text-[11px] sm:text-xs text-amber-200 break-all"
            data-testid="solana-wallet-address"
          >
            {solanaPayment?.wallet || "—"}
          </span>
          <CopyButton
            value={solanaPayment?.wallet || ""}
            label="wallet address"
            testid="copy-solana-wallet"
          />
        </div>

        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-mono mt-4">
          Step 2 · Paste your transaction signature:
        </div>
        <input
          type="text"
          value={sig}
          onChange={(e) => setSig(e.target.value)}
          placeholder="e.g. 5KqGn7…rXk"
          data-testid="solana-tx-signature"
          className="w-full bg-black/40 border border-zinc-800 focus:border-amber-500/60 px-3 py-2 text-sm text-zinc-100 font-mono outline-none"
        />
        <input
          type="text"
          value={sender}
          onChange={(e) => setSender(e.target.value)}
          placeholder="Your Phantom wallet address (optional, helps AI verify)"
          data-testid="solana-sender-wallet"
          className="w-full bg-black/40 border border-zinc-800 focus:border-amber-500/60 px-3 py-2 text-xs text-zinc-300 font-mono outline-none"
        />
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={240}
          placeholder="Note for the Architect (optional)"
          data-testid="solana-payment-note"
          className="w-full bg-black/40 border border-zinc-800 focus:border-amber-500/60 px-3 py-2 text-xs text-zinc-400 font-mono outline-none"
        />

        <button
          type="submit"
          disabled={busy || !sig.trim()}
          data-testid="pricing-cta-builder"
          className="w-full px-4 py-3 border border-amber-500/60 text-amber-200 hover:border-amber-300 hover:text-amber-100 hover:bg-amber-500/10 text-[11px] uppercase tracking-[0.3em] font-mono disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> AI auditor is verifying on-chain…
            </>
          ) : (
            <>
              <Shield className="h-4 w-4" /> Verify payment · unlock builder
            </>
          )}
        </button>

        {verdict && (
          <div
            data-testid="payment-verdict"
            className={`mt-2 border p-3 text-[11px] font-mono ${
              verdict.verified && verdict.farming_risk === "low"
                ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-200"
                : verdict.verified
                ? "border-amber-500/40 bg-amber-500/5 text-amber-200"
                : "border-rose-500/40 bg-rose-500/5 text-rose-200"
            }`}
          >
            <div className="flex items-center gap-1.5 mb-1 uppercase tracking-[0.2em] text-[9px]">
              {verdict.verified ? (
                <Shield className="h-3 w-3" />
              ) : (
                <AlertTriangle className="h-3 w-3" />
              )}
              ai verdict · {verdict.farming_risk} risk · confidence{" "}
              {Math.round((verdict.confidence || 0) * 100)}%
              {verdict.source && (
                <span className="ml-auto opacity-60">via {verdict.source}</span>
              )}
            </div>
            <div className="text-[11px] leading-snug">{verdict.reasoning}</div>
          </div>
        )}
      </form>
      </details>
    </article>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [supply, setSupply] = useState(null);
  const [membership, setMembership] = useState(null);
  const [error, setError] = useState(null);

  const loadAll = () => {
    Promise.all([
      getAegisPricing(),
      getAegisSupply(),
      getMembership().catch(() => null),
    ])
      .then(([p, s, m]) => {
        setPricing(p);
        setSupply(s);
        setMembership(m);
      })
      .catch((e) => setError(e?.message || String(e)));
  };

  useEffect(() => {
    loadAll();
  }, []);

  const tiers = pricing?.tiers || [];
  const free = tiers.find((t) => t.tier_id === "free");
  const builder = tiers.find((t) => t.tier_id === "builder");
  const network = tiers.find((t) => t.tier_id === "network_operator");
  const currentTier = membership?.tier || "free";

  return (
    <div className="min-h-[calc(100vh-130px)] bg-[#09090b] text-zinc-100">
      <LiveTicker fallbackMultiplier={supply?.burn_multiplier} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {/* Hero */}
        <div className="max-w-3xl">
          <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-2 inline-flex items-center gap-2">
            <Sparkles className="h-3 w-3" /> △ AEGIS · membership tiers
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-[0.1em] text-zinc-100 leading-tight">
            Help the world build a verifiable knowledge layer.
          </h1>
          <p className="text-zinc-400 text-base mt-4 leading-relaxed">
            Every tier mints{" "}
            <span className="text-amber-300 font-mono">AEGIS</span> on every
            verified truth — paying just unlocks more questions per day so
            people who can afford it can do more good. Pay in{" "}
            <span className="text-emerald-300 font-mono">AEGIS on Solana</span>{" "}
            — the network&apos;s own token pays for its own membership. An AI auditor
            cross-examines every payment on-chain and flags multi-account
            farming before any tier is granted.
          </p>
        </div>

        {/* Current quota / tier badge */}
        {membership && (
          <div
            className="mt-6 inline-flex flex-wrap items-center gap-3 border border-zinc-800 bg-[#0b0b0d] px-4 py-3 font-mono text-xs"
            data-testid="membership-status"
          >
            <span className="uppercase tracking-[0.3em] text-zinc-500 text-[10px]">
              your tier
            </span>
            <span
              className={`px-2 py-0.5 uppercase tracking-[0.25em] text-[10px] border ${
                membership.tier === "builder"
                  ? "border-amber-500/50 text-amber-300 bg-amber-500/5"
                  : membership.tier === "architect"
                  ? "border-sky-500/50 text-sky-300 bg-sky-500/5"
                  : "border-emerald-500/40 text-emerald-300 bg-emerald-500/5"
              }`}
              data-testid="membership-tier-badge"
            >
              {membership.tier}
            </span>
            {membership.expires_at && (
              <span className="text-zinc-500 text-[10px] uppercase tracking-[0.2em]">
                · expires {new Date(membership.expires_at).toLocaleDateString()}
              </span>
            )}
            {!membership.unbounded && (
              <span
                className="text-zinc-300 text-[10px] uppercase tracking-[0.2em]"
                data-testid="membership-quota"
              >
                · {membership.remaining}/{membership.limit} questions left today
              </span>
            )}
            {membership.unbounded && (
              <span className="text-sky-300 text-[10px] uppercase tracking-[0.2em]">
                · unlimited
              </span>
            )}
          </div>
        )}

        {/* Economics strip */}
        {pricing && (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="border border-zinc-800 bg-[#0b0b0d] p-4" data-testid="econ-rate">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1 font-mono">
                Token rate
              </div>
              <div className="font-mono text-xl text-amber-300 tabular-nums">
                ${pricing.proof_usd_per_million}
                <span className="text-zinc-500 text-xs"> / 1M PROOF</span>
              </div>
              <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-600 mt-1 font-mono">
                1 APT = {pricing.proof_per_apt} PROOF
              </div>
            </div>
            <div className="border border-rose-500/40 bg-rose-500/5 p-4" data-testid="econ-burn">
              <div className="text-[10px] uppercase tracking-[0.3em] text-rose-400 mb-1 flex items-center gap-1 font-mono">
                <Flame className="h-3 w-3" /> Burn ratio
              </div>
              <div className="font-mono text-xl text-rose-300 tabular-nums">
                {(pricing.burn_ratio * 100).toFixed(0)}%
              </div>
              <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-600 mt-1 font-mono">
                of every payment
              </div>
            </div>
            <div className="border border-zinc-800 bg-[#0b0b0d] p-4" data-testid="econ-retained">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1 font-mono">
                Retained
              </div>
              <div className="font-mono text-xl text-zinc-200 tabular-nums">
                {(pricing.retained_ratio * 100).toFixed(0)}%
              </div>
              <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-600 mt-1 font-mono">
                operating revenue
              </div>
            </div>
            <div className="border border-emerald-500/40 bg-emerald-500/5 p-4" data-testid="econ-multiplier">
              <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400 mb-1 flex items-center gap-1 font-mono">
                <TrendingUp className="h-3 w-3" /> Current uplift
              </div>
              <div className="font-mono text-xl text-emerald-300 tabular-nums">
                ×{Number(supply?.burn_multiplier || 1).toFixed(
                  (supply?.burn_multiplier || 1) < 10 ? 4 : 2
                )}
              </div>
              <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-600 mt-1 font-mono">
                per existing coin
              </div>
            </div>
          </div>
        )}

        {/* Tiers */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
          {error && (
            <div
              className="sm:col-span-3 border border-rose-500/40 bg-rose-500/5 text-rose-300 p-4 text-sm font-mono"
              data-testid="pricing-error"
            >
              {String(error)}
            </div>
          )}
          {free && <FreeTierCard tier={free} currentTier={currentTier} />}
          {builder && (
            <BuilderTierCard
              tier={builder}
              solanaPayment={pricing?.solana_payment}
              usdcPayment={pricing?.usdc_stable_payment}
              paypalPayment={pricing?.paypal_payment}
              currentTier={currentTier}
              onPaid={loadAll}
              isAuthed={!!user?.user_id}
            />
          )}
          {network && <NetworkOperatorCard tier={network} />}
        </div>

        {/* Footer notes */}
        <div className="mt-12 border-t border-zinc-900 pt-6 text-xs text-zinc-500 leading-relaxed max-w-3xl">
          <div className="flex items-start gap-2 mb-2">
            <Layers className="h-4 w-4 text-zinc-600 mt-0.5 shrink-0" />
            <div>
              <span className="text-zinc-300 font-mono uppercase tracking-[0.2em] text-[10px]">
                Non-monetary disclaimer:
              </span>{" "}
              {pricing?.notes ||
                "AEGIS coins are non-monetary thank-you badges. They are not securities, are not redeemable, and confer no claim against the Sentinel beyond the public proof-of-discovery record."}
            </div>
          </div>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/aegis/pricing`}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1.5 text-amber-300/70 hover:text-amber-300 text-[10px] uppercase tracking-[0.3em] font-mono mt-4"
            data-testid="pricing-raw-link"
          >
            <ExternalLink className="h-3 w-3" /> raw pricing JSON
          </a>
        </div>
      </div>
    </div>
  );
}
